import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import fetchUtil from '../../../utils/fetchUtil';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { MetaTagsComp } from '../../../components/MetaTagsComp';
import ErrorComponent from '../../../components/ErrorComponent';
import UniversityDetail from '../../../components/universityDetail/UniversityDetailComponent';
import { CACHE_REVALIDATE } from '../../../utils/constants';
import type { IntroAdData } from '../../../components/universityDetail/IntroAd/IntroAd';

const formatCountry = (country: string) => {
  let formattedSlug = country
    .toLowerCase()
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  // Handle special cases
  if (formattedSlug === 'Usa') {
    formattedSlug = 'USA';
  } else if (formattedSlug === 'Uk') {
    formattedSlug = 'UK';
  } else if (formattedSlug === 'Uae') {
    formattedSlug = 'UAE';
  }

  return formattedSlug;
};

const UniversityDetailPage: React.FC = (props: any) => {
  const router = useRouter();
  const { country, university } = router.query;

  useEffect(() => {
    const customStyle = document.documentElement.style;
    customStyle.setProperty('--navbar-color', '#313145');
    customStyle.setProperty('--navbar-menu-color', '#ffff');
  }, []);

  // Ensure universityDetails is not undefined or empty
  if (!props.universityDetails || Object.keys(props.universityDetails).length === 0) {
    return <ErrorComponent />;
  }

  // Convert university details into an array if needed
  const universityArray = Array.isArray(props.universityDetails)
    ? props.universityDetails
    : Object.values(props.universityDetails || {});

  if (!universityArray || universityArray.length === 0) {
    return <ErrorComponent />;
  }

  const filteredUniversityArray = universityArray.filter((uni: any) => {
    const formattedCountrySlug = (country as string ?? '').toLowerCase().trim();
    const countryFromObject = uni.country
      ?.replace(/([a-z])([A-Z])/g, "$1-$2")
      .toLowerCase()
      .trim();
    return countryFromObject === formattedCountrySlug;
  });

  const universityData = filteredUniversityArray.find((uni: any) => {
    const formattedPageSlug = (university as string ?? '').toLowerCase().trim();
    return uni?.slug?.toLowerCase().trim() === formattedPageSlug;
  });

  if (!universityData) {
    return <ErrorComponent />;
  }

  return (
    <>
      <MetaTagsComp {...universityData?.meta} />
      <UniversityDetail
        universityData={universityData}
        advertisements={props.advertisements}
        mobileAdvertisements={props.mobileAdvertisements}
        introAd={props.introAd}
      />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return { 
    paths: [], 
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const country = formatCountry(context?.params?.country as string ?? '');
  const universitySlug = context?.params?.university as string;

  try {
    let universityDetails: any = null;

    if (country && universitySlug) {
      universityDetails = await fetchUtil(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/universities?_sort=priority&country=${country}&slug=${universitySlug}`,
        'GET',
        {},
        '',
        false
      );
    }

    // Not found: return a real 404 instead of a 200 error page, which the CDN
    // would cache as valid and never self-heal. Same empty-check the component uses.
    if (!universityDetails || Object.keys(universityDetails).length === 0) {
      return { notFound: true, revalidate: 60 };
    }

    let advertisements: { url: string; href: string }[] = [];
    let mobileAdvertisements: { url: string; href: string }[] = [];
    let introAd: IntroAdData = {};
    try {
      const adsData = await fetchUtil(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/university-advertisements?country=${country}`,
        'GET',
        {},
        '',
        false
      );
      const matchedAd = Array.isArray(adsData) ? adsData.find((ad: any) => ad.country === country) : null;
      if (matchedAd) {
        advertisements = (matchedAd.advertisement ?? []).map((ad: any) => ({ url: ad.url, href: matchedAd.link }));
        mobileAdvertisements = (matchedAd.mobileAdvertisement ?? []).map((ad: any) => ({ url: ad.url, href: matchedAd.link }));
        introAd = matchedAd?.introAd ?? {}
      }
    } catch (adErr) {
      advertisements = [];
      mobileAdvertisements = [];
      introAd = {}
    }

    return {
      props: {
        universityDetails,
        advertisements,
        mobileAdvertisements,
        introAd
      },
      revalidate: CACHE_REVALIDATE
    };
  } catch (err) {
    // Fetch failed (CMS blip): 404 with a short revalidate so a real page
    // recovers within ~1 min instead of being frozen for the full window.
    return { notFound: true, revalidate: 60 };
  }
};

export default UniversityDetailPage; 